// consts/enums
import type { CalculationEndpointType, VehicleInsurance } from '@makemydeal/dr-platform-types';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { setCustomerToSync } from './editCustomerActionCreators';
import type { TradeInSlideoutTab } from '../types/types';
import { BALLOON, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import {
    SET_BREAKDOWN,
    SET_ACTIVE_BREAKDOWN_TAB_DETAILS,
    SAVE_VEHICLE_INSURANCE_INFO,
    SHOW_CREDIT_BUREAU,
    TAB_CHANGE,
    SHOW_BREAKDOWN_SLIDEOUT,
    SHOW_CUSTOMER_INSIGHTS_SLIDEOUT,
    SHOW_REPAIR_ORDERS_SLIDEOUT,
    INCENTIVES_SLIDEOUT_TAB_CHANGE,
    SET_FUNCTIONS_MODAL,
    TOGGLE_RATE_TYPE,
    SET_REFERENCE_RATE_TYPE,
    SET_AUDIT_LOG_SLIDEOUT,
    COMPLIANCE_SLIDEOUT_TAB_CHANGE,
    TRADE_IN_SLIDEOUT_TAB_CHANGE,
    SET_IS_AUTOSELECT_PROGRAM_CHECKED,
    SET_CALC_ENGINE_TRANSITION_STATE,
    CLEAR_AIO_DXG_EVENT_ERRORS,
    DISMISS_FS_SYNC_FAILURE,
    FS_SYNC_FINAL_ATTEMPT_TIMED_OUT,
    OPEN_SLIDEOUT,
    SHOW_UPDATE_OFFER_FOR_FS_EVENTS,
    CLOSE_SLIDEOUT,
    SET_PENDING_NAVIGATION,
    CLEAR_PENDING_NAVIGATION,
    UPDATE_RIGHT_TO_CANCEL_DAYS,
    UPDATE_RIGHT_TO_CANCEL_CHARGE,
    DEAL_PULSE_SLIDEOUT_VIEWED
} from '../actionTypes/baseDealActionTypes';
import { SET_SLIDEOUT_CONTENT, SHOW_CUSTOMER_RISK_AND_COVERAGE_SLIDEOUT } from '../actionTypes/customerRiskAndCoverageActionTypes';
import { customerRiskAndCoverageSlideOutButton } from './customerRiskAndCoverageActionCreators';
import type { Dispatch } from 'react';
import type { AnyAction } from 'redux';
import * as offerRedux from './offerRedux';
import type { CalcEngineTransitionStateTypes } from '../types/calcEngineTransitionStateTypes';
import { scopedActionsCreators, scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import axios from 'axios';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { getCurrentAxiosRequestInterceptor } from '@makemydeal/dr-shared-network';
import * as baseDealSelectors from '../selectors/BaseDealSelectors';
import type { VehicleAnalyticsPayload } from '../types/analyticsPayloadTypes';
import type { ConsumerInsightsState, TradeInIntelligenceAnalyticsRecord } from '@makemydeal/dr-dash-types';

export const setBreakdownStatus = (show: boolean, breakdownActionSource?: string) => ({
    type: SET_BREAKDOWN,
    payload: { showBreakdown: show, breakdownActionSource }
});

export const saveVehicleInsuranceInfo = (vehicleInsuranceInfo: VehicleInsurance) => {
    return {
        type: SAVE_VEHICLE_INSURANCE_INFO,
        payload: {
            ...vehicleInsuranceInfo
        }
    };
};

export const updateVehicleInsuranceInfo = (vehicleInsuranceData: VehicleInsurance, dealXgId: string) => {
    return async (dispatch: Dispatch<AnyAction>, getState: () => any) => {
        try {
            // Filter out null/undefined date fields from the payload
            const filteredPayload = { ...vehicleInsuranceData };

            // Remove policyStartDate if it's null, undefined, or falsy
            if (!filteredPayload.policyStartDate) {
                delete filteredPayload.policyStartDate;
            }

            // Remove policyExpirationDate if it's null, undefined, or falsy
            if (!filteredPayload.policyExpirationDate) {
                delete filteredPayload.policyExpirationDate;
            }

            // If agentAddress is present but stateProvince is null/undefined, remove stateProvince
            if (filteredPayload.agentAddress && !filteredPayload.agentAddress.stateProvince) {
                delete filteredPayload.agentAddress.stateProvince;
            }

            // If agentPhoneNumber is null/undefined/falsy, remove it from the payload
            if (!filteredPayload.agentPhoneNumber) {
                delete filteredPayload.agentPhoneNumber;
            }

            const locHref = locHrefUtil.getLocHref();
            const endpoint = apiConfigHelper.buildBffApiUrl(
                MANAGER_VIEW_APP_PREFIX,
                locHref,
                `/deal/${dealXgId}/vehicleInsurance`
            ).url;

            const axiosRequestInterceptor = getCurrentAxiosRequestInterceptor();
            const { headers } = await axiosRequestInterceptor({ url: endpoint });

            const response = await axios.post(endpoint, filteredPayload, {
                headers: {
                    Authorization: headers.Authorization
                }
            });

            // Update Redux store with the response from API
            dispatch(saveVehicleInsuranceInfo(response.data));

            // Mark the buyer dirty so an insurance-only edit still triggers the Common Consumer sync.
            if (featureToggleSelectors.isSyncCustomerCardFieldsEnabled(getState())) {
                const { commonConsumerId } = offerReduxSelectors.getShopperInfo(getState());
                if (commonConsumerId) {
                    dispatch(setCustomerToSync(commonConsumerId));
                }
            }

            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error };
        }
    };
};

export const setCreditBureauStatus = (show: boolean) => ({
    type: SHOW_CREDIT_BUREAU,
    payload: { showCreditBureau: show }
});

export const setCustomerRiskAndCoverageSlideOutStatus = (show: boolean) => ({
    type: SHOW_CUSTOMER_RISK_AND_COVERAGE_SLIDEOUT,
    payload: { showCustomerRiskAndCoverageSlideOut: show }
});

export const handleTabChange = (tab: string) => (dispatch: any, getState: any) => {
    const action = { type: TAB_CHANGE, payload: { activeTab: tab } };
    dispatch(action);

    const state = getState();
    if (featureToggleSelectors.isDealScreenRedDotErrorsEnabled(state) && scopedStateSelectors.hasMultipleActiveScopes(state)) {
        dispatch(scopedActionsCreators.dispatchOnEveryScope(action, { ignoreSelf: true }));
    }
};

export const setBreakdownSlideoutStatus = (show: boolean) => ({
    type: SHOW_BREAKDOWN_SLIDEOUT,
    payload: { showBreakdownSlideout: show }
});

export const setCustomerInsightsSlideoutStatus = (show: boolean) => ({
    type: SHOW_CUSTOMER_INSIGHTS_SLIDEOUT,
    payload: { showCustomerInsightsSlideout: show }
});

/**
 * dc-mv-306: View the DealPulse slideout (Customer Insights panel) with the customer's DealPulse predictions.
 */
export const dealPulseSlideoutViewed = (
    vehicleInfo: VehicleAnalyticsPayload | undefined,
    dealPulseInsights: ConsumerInsightsState,
    tradeInRecord: TradeInIntelligenceAnalyticsRecord | null
) => ({
    type: DEAL_PULSE_SLIDEOUT_VIEWED,
    payload: { vehicleInfo, dealPulseInsights, tradeInRecord }
});

/**
 * The drawer is a single page-level instance, outside any scenario's scope, so opening it has to say
 * which car it is opening for — otherwise it renders whichever repair orders the primary scenario holds.
 */
export const setRepairOrdersSlideoutStatus = (show: boolean, vin?: string) => ({
    type: SHOW_REPAIR_ORDERS_SLIDEOUT,
    payload: { showRepairOrdersSlideout: show, repairOrdersSlideoutVin: vin }
});

export const setActiveBreakdownTabDetails = (activeTabDetails: Record<string, string>) => ({
    type: SET_ACTIVE_BREAKDOWN_TAB_DETAILS,
    payload: { activeBreakdownTabDetails: activeTabDetails }
});

export const setIncentivesSlideoutTab = (tab: 'included' | 'manualUpdates') => ({
    type: INCENTIVES_SLIDEOUT_TAB_CHANGE,
    payload: { activeTab: tab }
});

export const setCustomerRiskAndCoverageTab = (tab: 'insurance' | 'compliance' | 'credit_bureau') => ({
    type: COMPLIANCE_SLIDEOUT_TAB_CHANGE,
    payload: { activeTab: tab }
});

export const setTradeInSlideoutTab = (tab: TradeInSlideoutTab) => ({
    type: TRADE_IN_SLIDEOUT_TAB_CHANGE,
    payload: { activeTab: tab }
});

export const setFunctionsModal = (show: boolean) => ({
    type: SET_FUNCTIONS_MODAL,
    payload: { showFunctionsModal: show }
});

export const toggleRateType = (rateType: string, selectedTerm: string, offerType: string, months: number | undefined) => ({
    type: TOGGLE_RATE_TYPE,
    payload: { rateType, selectedTerm, offerType, months }
});

export const setReferenceRateType = (rateType: string | undefined) => ({
    type: SET_REFERENCE_RATE_TYPE,
    payload: { rateType }
});

export const setAuditLogSlideoutStatus = (show: boolean) => ({
    type: SET_AUDIT_LOG_SLIDEOUT,
    payload: { showAuditLogSlideout: show }
});

export const showSlideOutContent = (content: React.ReactNode) => ({
    type: SET_SLIDEOUT_CONTENT,
    payload: { slideOutContent: content }
});

export const openCustomerRiskAndCoverageSlideOut =
    (mode: 'insurance' | 'compliance' | 'credit_bureau', content?: React.ReactNode) => (dispatch: Dispatch<AnyAction>) => {
        dispatch(setCustomerRiskAndCoverageTab(mode));
        dispatch(setCustomerRiskAndCoverageSlideOutStatus(true));
        dispatch(customerRiskAndCoverageSlideOutButton());
        if (content) {
            dispatch(showSlideOutContent(content));
        }
    };

export const setCalcEngineTransitionState = (payload?: Partial<CalcEngineTransitionStateTypes>) => {
    return {
        type: SET_CALC_ENGINE_TRANSITION_STATE,
        payload
    };
};

export const updateCalculationEndpointForFinanceAndLeaseType =
    (endPoint: CalculationEndpointType) => (dispatch: Dispatch<AnyAction>) => {
        dispatch(offerRedux.updateCurrentCalculationEndpoint(endPoint, FINANCE));
        dispatch(offerRedux.updateCurrentCalculationEndpoint(endPoint, LEASE));
        dispatch(offerRedux.updateCurrentCalculationEndpoint(endPoint, BALLOON));
    };

export const setIsAutoSelectProgramChecked = (isAutoSelectProgramChecked: boolean) => {
    return {
        type: SET_IS_AUTOSELECT_PROGRAM_CHECKED,
        payload: isAutoSelectProgramChecked
    };
};

export const clearAioDxgEventErrors = () => {
    return {
        type: CLEAR_AIO_DXG_EVENT_ERRORS
    };
};

export const dismissFsSyncFailure = () => {
    return {
        type: DISMISS_FS_SYNC_FAILURE
    };
};

export const fsSyncFinalAttemptTimedOut = () => {
    return {
        type: FS_SYNC_FINAL_ATTEMPT_TIMED_OUT
    };
};

// SlideOut type enum - keep in sync with activities-desking
export enum SlideOutType {
    Accessories = 'Accessories',
    Appraisal = 'Appraisal',
    ChangeVehicle = 'ChangeVehicle',
    FirstPencilChangeVehicle = 'FirstPencilChangeVehicle',
    FirstPencilIncentives = 'FirstPencilIncentives',
    Fees = 'Fees',
    Incentives = 'Incentives',
    Lender = 'Lender',
    Protection = 'Protection',
    Taxes = 'Taxes',
    TradeIn = 'TradeIn',
    None = 'none'
}

export const openSlideOut =
    (slideOutType: SlideOutType, scopeIdOverride?: string, appraisalTradeInIndex?: number) => (dispatch: any, getState: any) => {
        const state = getState();
        // Use the provided scope ID override, or fall back to the current scope ID from state
        const currentScopeId = scopeIdOverride || scopedStateSelectors.getCurrentScopeId(state as any);
        const currentSlideOut = baseDealSelectors.getCurrentSlideOut(state);

        // Set the navigation scope target so slideout content knows which scenario's data to use
        dispatch(scopedActionsCreators.setNavigationScopeTarget(currentScopeId));

        // Open the slideout. The Appraisal slide-out is opened on top of the Trade-In slide-out
        // (via the "View in vAuto" link), so track the slide-out it's covering as previousSlideOut -
        // closing Appraisal then returns to Trade-In instead of closing everything.
        dispatch({
            type: OPEN_SLIDEOUT,
            payload: slideOutType,
            previousSlideOut: slideOutType === SlideOutType.Appraisal ? currentSlideOut : undefined,
            appraisalTradeInIndex
        });
    };

export const closeSlideOut = () => ({
    type: CLOSE_SLIDEOUT
});

export const setShowUpdateOfferForFSEvents = (show: boolean) => ({
    type: SHOW_UPDATE_OFFER_FOR_FS_EVENTS,
    payload: { showUpdateOfferForFSEvents: show }
});

/**
 * Stores a pending navigation URL to be executed after unsaved changes are handled
 */
export const setPendingNavigation = (url: string) => ({
    type: SET_PENDING_NAVIGATION,
    payload: { url }
});

/**
 * Clears the pending navigation state
 */
export const clearPendingNavigation = () => ({
    type: CLEAR_PENDING_NAVIGATION
});

export const updateRightToCancelDays = (rightToCancelDays: number | undefined) => ({
    type: UPDATE_RIGHT_TO_CANCEL_DAYS,
    payload: { rightToCancelDays }
});

export const updateRightToCancelCharge = (rightToCancelCharge: number | undefined) => ({
    type: UPDATE_RIGHT_TO_CANCEL_CHARGE,
    payload: { rightToCancelCharge }
});
